import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { message } from 'antd';
import api from '../utils/api';

const AuthContext = createContext();

const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  loading: true,
  isAuthenticated: false
};

function authReducer(state, action) {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      localStorage.setItem('token', action.payload.token);
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false
      };
    case 'LOGOUT':
      localStorage.removeItem('token');
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false
      };
    case 'LOAD_USER_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        loading: false
      };
    case 'LOAD_USER_FAIL':
      localStorage.removeItem('token');
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      };
    default:
      return state;
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // 加载用户信息
  const loadUser = useCallback(async () => {
    if (state.token) {
      try {
        const response = await api.get('/auth/me');
        dispatch({ type: 'LOAD_USER_SUCCESS', payload: response.data.user });
      } catch (error) {
        console.error('加载用户信息失败:', error.message);
        dispatch({ type: 'LOAD_USER_FAIL' });
      }
    } else {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state.token]);

  // 登录
  const login = async (credentials) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await api.post('/auth/login', credentials);
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: response.data.user,
          token: response.data.token
        }
      });
      
      message.success('登录成功');
      return { success: true };
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      const errorMessage = error.response?.data?.message || '登录失败';
      message.error(errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  // 登出
  const logout = (options = {}) => {
    dispatch({ type: 'LOGOUT' });
    if (!options.silent) message.success('已退出登录');
  };

  // 修改密码
  const changePassword = async (passwords) => {
    try {
      await api.put('/auth/change-password', passwords, { silentError: true });
      dispatch({ type: 'LOGOUT' });
      message.success('密码已修改，请重新登录');
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || '密码修改失败';
      return { success: false, message: errorMessage };
    }
  };

  // 检查权限
  const hasPermission = (resource, action) => {
    if (!state.user) return false;
    if (state.user.role === 'super_admin') return true;
    const permissions = state.user.permissions || {};
    return Boolean(permissions[resource]?.[action]);
  };

  // 检查角色
  const hasRole = (roles) => {
    if (!state.user) return false;
    return roles.includes(state.user.role);
  };

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const value = {
    ...state,
    login,
    logout,
    changePassword,
    hasPermission,
    hasRole,
    loadUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
